"use client";

import { useEffect, useRef, useState } from "react";

type CounterProps = {
  to: number;
  suffix?: string;
  className?: string;
};

const Counter = ({ to, suffix = "", className }: CounterProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(to);
      return;
    }

    let raf = 0;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);

          const t0 = performance.now();
          const step = (t: number) => {
            const p = Math.min(1, (t - t0) / 1400);
            setValue(to * (1 - Math.pow(1 - p, 3)));
            if (p < 1) raf = requestAnimationFrame(step);
          };
          raf = requestAnimationFrame(step);
        });
      },
      { threshold: 0.5 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [to]);

  const display = to % 1 ? value.toFixed(1) : Math.round(value);

  return (
    <div ref={ref} className={className}>
      {display}
      {suffix}
    </div>
  );
};

export default Counter;
